/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/quotes */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, concat, Observable, Subject, Subscription } from 'rxjs';
import { BLEService, Device, DeviceCharacteristicData } from './ble.service';
import { mergeAll, mergeMap, subscribeOn, takeUntil } from 'rxjs/operators';
import { EmployeeV2Dto, ImplementV2Dto } from '../api/exa';

export enum AdapterConnectionStateEnum {
  DISCONNECTED = 'DISCONNECTED',
  SCANNING = 'SCANNING',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export class AutoReconnectSettings {
  serial: string;
  mac?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdapterConnectionService {
  public static SERIAL_SERVICE_UUID_T2: string = '0000ffe0-0000-1000-8000-00805f9b34fb';
  public static SERIAL_CHARACTERISTIC_TX_UUID_T2: string = '0000ffe0-0000-1000-8000-00805f9b34fb';
  public static SERIAL_CHARACTERISTIC_RX_UUID_T2: string = '0000ffe1-0000-1000-8000-00805f9b34fb';

  public static SERIAL_SERVICE_UUID_T3: string = '2456e1b9-26e2-8f83-e744-f34f01e9d701';
  public static SERIAL_CHARACTERISTIC_TX_RX_UUID_T3: string = '2456e1b9-26e2-8f83-e744-f34f01e9d703';

  private static CR: string = '\r';

  private static BLE_CHUNK_SIZE: number = 20;

  private bleEnabled = false;

  private connectionState: AdapterConnectionStateEnum = AdapterConnectionStateEnum.DISCONNECTED;

  // connection handling
  private scanSub: Subscription = null;
  private deviceConnectionStatusSub: Subscription = null;
  private device: Device = null;

  private isHandlingCommand: boolean = false;

  private autoReconnect: AutoReconnectSettings = null;

  private onConnectionStateChanged$: BehaviorSubject<AdapterConnectionStateEnum> = new BehaviorSubject(
    AdapterConnectionStateEnum.DISCONNECTED
  );

  private unsubscribe$ = new Subject<void>();

  private charateristicReadSub = null;
  private readData$ = new Subject<string>();

  private encoder: TextEncoder = new TextEncoder(); // utf-8
  private decoder: TextDecoder = new TextDecoder('utf-8'); // utf-8

  private serviceUUID = null;
  private rxCharacteristicUUID = null;
  private txCharacteristicUUID = null;

  constructor(
    private log: NGXLogger,
    private bleService: BLEService
  ) {
    this.bleService
      .onIsEnabledChanged()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isEnabled) => {
        if (isEnabled != null) {
          this.bleEnabled = isEnabled;
        }
      });
  }

  ngOnDestroy(): void {
    this.log.debug('Destroy');
    this.resetConnectionVars();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  connectToAdapter(serial: string, mac?: string, autoReconnect?: boolean) {
    this.log.debug('Connecting to adapter', serial, mac);
    if (!this.bleEnabled) {
      this.log.error('BLE not enabled');
      return;
    }
    if (this.connectionState != AdapterConnectionStateEnum.DISCONNECTED) {
      this.log.error('Another connection process is already running');
      return;
    }
    if (!serial) {
      this.log.error('No serial given');
      return;
    }

    this.connectionState = AdapterConnectionStateEnum.SCANNING;
    this.onConnectionStateChanged$.next(this.connectionState);

    if (!autoReconnect) {
      this.autoReconnect = null;
    } else {
      this.autoReconnect = { serial: serial, mac: mac };
    }

    const serviceUUIDsFilter = [AdapterConnectionService.SERIAL_SERVICE_UUID_T2, AdapterConnectionService.SERIAL_SERVICE_UUID_T3];
    this.log.debug('starting scan for service UUIDs', serviceUUIDsFilter);
    this.scanSub = this.bleService.scan(serviceUUIDsFilter).subscribe(
      (devices) => {
        if (devices) {
          this.log.debug('discovered devices', devices);
          // check if any device matches the adapter we want to connect to
          for (const device of devices) {
            if (device.name == serial || device.id == mac) {
              // TODO maybe we have to remove the ':' from device.id to match our mac syntax
              // found suiting device
              this.connectionState = AdapterConnectionStateEnum.CONNECTING;
              this.onConnectionStateChanged$.next(this.connectionState);
              this.device = device;
              // stop scanning
              this.scanSub.unsubscribe();
              this.scanSub = null;
              this.bleService.cancelScan();

              this.serviceUUID = AdapterConnectionService.SERIAL_SERVICE_UUID_T2;
              this.rxCharacteristicUUID = AdapterConnectionService.SERIAL_CHARACTERISTIC_RX_UUID_T2;
              this.txCharacteristicUUID = AdapterConnectionService.SERIAL_CHARACTERISTIC_TX_UUID_T2;
              if (serial.startsWith('EXA-T3')) {
                this.serviceUUID = AdapterConnectionService.SERIAL_SERVICE_UUID_T3;
                this.rxCharacteristicUUID = AdapterConnectionService.SERIAL_CHARACTERISTIC_TX_RX_UUID_T3;
                this.txCharacteristicUUID = AdapterConnectionService.SERIAL_CHARACTERISTIC_TX_RX_UUID_T3;
              }

              // connect to device
              this.deviceConnectionStatusSub = this.bleService.connectToDevice(device.id).subscribe((deviceConnectionStatus) => {
                if (deviceConnectionStatus && deviceConnectionStatus.deviceId == this.device.id) {
                  if (deviceConnectionStatus.connected) {
                    // device connected successfully
                    this.connectionState = AdapterConnectionStateEnum.CONNECTED;

                    // setup read characteristic
                    this.charateristicReadSub = this.bleService
                      .readCharacteristicMultiple(this.device.id, this.serviceUUID, this.rxCharacteristicUUID)
                      .subscribe(
                        (characData) => {
                          this.readData$.next(characData.data);
                        },
                        (error) => {}
                      );

                    this.onConnectionStateChanged$.next(this.connectionState);
                  } else {
                    // device disconnected (connection lost or whatever) -> try to reconnect immediately if autoReconnect is active
                    this.connectionState = AdapterConnectionStateEnum.DISCONNECTED;
                    this.resetConnectionVars();
                    this.onConnectionStateChanged$.next(this.connectionState);
                    if (this.autoReconnect) {
                      this.connectToAdapter(this.autoReconnect.serial, this.autoReconnect.mac, true);
                    } else {
                      // only send state change if not auto reconnecting
                      this.onConnectionStateChanged$.next(this.connectionState);
                    }
                  }
                }
              });
            }
          }
        }
      },
      (error) => {
        this.log.error('scanning error', error);
        // rest is handled in complete below
      },
      () => {
        // complete -> scanning finished
        this.log.error('SCANNING FINISHED');
        this.log.error('CONECTION STATE', this.connectionState);
        if (this.connectionState == AdapterConnectionStateEnum.SCANNING) {
          this.connectionState = AdapterConnectionStateEnum.DISCONNECTED;
          this.resetConnectionVars();
          if (this.autoReconnect) {
            this.connectToAdapter(this.autoReconnect.serial, this.autoReconnect.mac, true);
          } else {
            // only send state change if not auto reconnecting
            this.onConnectionStateChanged$.next(this.connectionState);
          }
        }
      }
    );
  }

  disconnect() {
    this.cancelAutoReconnection();
    this.bleService.cancelScan();
    this.resetConnectionVars();
    if (this.device) {
      this.bleService.disconnectFromDevice(this.device.id);
    }
    this.connectionState = AdapterConnectionStateEnum.DISCONNECTED;
    this.onConnectionStateChanged$.next(this.connectionState); // this may be doubled with deviceConnectionStatusSub
  }

  private cancelAutoReconnection() {
    this.autoReconnect = null;
  }

  private resetConnectionVars() {
    this.resetReading();
    if (this.scanSub) {
      this.scanSub.unsubscribe();
      this.scanSub = null;
    }
    if (this.deviceConnectionStatusSub) {
      this.deviceConnectionStatusSub.unsubscribe();
      this.deviceConnectionStatusSub = null;
    }
    this.device = null;
  }

  //
  // DATA TRANSFER FUNCTIONS
  //
  getAdapterStatus(): Promise<string> {
    this.log.debug('Getting adapter status');
    return new Promise((resolve, reject) => {
      let command: string = 'XA STATE';
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  getSyncStatus(): Promise<string> {
    this.log.debug('Getting sync status');
    return new Promise((resolve, reject) => {
      let command: string = 'XA SYN';
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  getGPSStatus(): Promise<string> {
    this.log.debug('Getting GSP status');
    return new Promise((resolve, reject) => {
      let command: string = 'XA GS';
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  getCANStatus(): Promise<string> {
    this.log.debug('Getting CAN status');
    return new Promise((resolve, reject) => {
      let command: string = 'XA CSE';
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  getISOBUSStatus(): Promise<string> {
    this.log.debug('Getting ISOBUS status');
    return new Promise((resolve, reject) => {
      let command: string = 'XA ISO';
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  getData(paramId): Promise<string> {
    this.log.debug('Getting data parameter ' + paramId);
    return new Promise((resolve, reject) => {
      let command: string = 'XA DA:' + paramId;
      this.sendCommand(command).then((answer) => {
        let state: string = this.checkGetCommandAnswerAndGetData(answer);
        resolve(state);
      });
    });
  }

  setDriver(employee: EmployeeV2Dto): Promise<void> {
    return new Promise((resolve, reject) => {
      let command: string = null;
      if (employee.id) {
        command = 'XA DRV:' + employee.id;
      } else {
        command = 'XA DRVU:' + employee.username;
      }
      this.sendCommand(command).then(
        (answer) => {
          let driverData: string = this.checkSetCommandAnswerAndGetData(answer);
          if (employee.id == parseInt(driverData) || employee.username == driverData) {
            resolve();
          } else {
            reject('driver data mismatch: ' + employee + ' / BLE data: ' + driverData);
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  setImplement(implement: ImplementV2Dto): Promise<void> {
    return new Promise((resolve, reject) => {
      let command: string = 'XA IMP:' + implement.id;
      this.sendCommand(command).then(
        (answer) => {
          let implementId: string = this.checkSetCommandAnswerAndGetData(answer);
          if (implement.id == parseInt(implementId)) {
            resolve();
          } else {
            reject('implement id mismatch: ' + implement.id + ' / BLE data: ' + implementId);
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  private checkSetCommandAnswerAndGetData(answer: string): string {
    if (answer) {
      if (answer.startsWith('S:')) {
        return answer.replace('S:', '');
      } else {
        throw new Error('incorrect set command answer format');
      }
    } else {
      throw new Error('no set command answer');
    }
  }

  private checkGetCommandAnswerAndGetData(answer: string): string {
    if (answer) {
      if (answer.startsWith('G:')) {
        return answer.replace('G:', '');
      } else {
        throw new Error('incorrect get command answer format');
      }
    } else {
      throw new Error('no get command answer');
    }
  }

  private sendCommand(command: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (this.connectionState != AdapterConnectionStateEnum.CONNECTED) {
        this.log.error('unsufficient connection state', this.connectionState);
        reject(this.connectionState);
      }
      if (this.isHandlingCommand) {
        this.log.error('already handling another command');
        reject('handlingOtherCommand');
      }
      this.isHandlingCommand = true;

      let resultString = '';

      // also add the CR
      command = command + AdapterConnectionService.CR;

      // TODO 2 bytes for each char -> 20 bytes per ble package? -> only 10 characters?! -> on android 20 characters worked
      let commandChunks: string[];
      if (command.length > AdapterConnectionService.BLE_CHUNK_SIZE) {
        // split command in chunks of size BLE_CHUNK_SIZE
        let chunk = '';
        for (const char of command) {
          if (chunk.length < AdapterConnectionService.BLE_CHUNK_SIZE) {
            // add to chunk
            chunk += char;
          } else {
            // create new chunk and add the current chunk to the command parts list
            commandChunks.push(chunk);
            chunk = char;
          }
        }
        // also add the current unfinished chunk
        commandChunks.push(chunk);
      } else {
        commandChunks = [command];
      }

      const readingSub = this.readData$.subscribe((data) => {
        // concatenate data to form result string
        resultString += data;
        // check if ends with terminating string -> resolve promise and isHandlingCommand = false
        // answer is terminated by carriage return char
        if (resultString.endsWith(AdapterConnectionService.CR)) {
          readingSub.unsubscribe();
          this.isHandlingCommand = false;
          resolve(resultString.replace(AdapterConnectionService.CR, ''));
        }
      });

      const writePromises: Promise<DeviceCharacteristicData>[] = [];
      for (const chunk of commandChunks) {
        writePromises.push(this.bleService.writeCharacteristic(this.device.id, this.serviceUUID, this.txCharacteristicUUID, chunk));
      }

      //concat all command writes
      Promise.all(writePromises).then(
        (writeResults) => {
          // every write succeeded
          // reading is done in parallel with code above
        },
        (error) => {
          // stop multiple read
          this.isHandlingCommand = false;
          reject(error);
        }
      );
    });
  }

  private resetReading(): Promise<void> {
    if (this.device) {
      if (this.charateristicReadSub) {
        this.charateristicReadSub.unsubscribe();
      }
      return new Promise((resolve, reject) => {
        this.bleService.cancelReadCharacteristicMultiple(this.device.id, this.serviceUUID, this.rxCharacteristicUUID).then(
          (cancelled) => {
            this.isHandlingCommand = false;
            resolve();
          },
          (error) => {
            this.isHandlingCommand = false;
            reject(error);
          }
        );
      });
    } else {
      this.isHandlingCommand = false;
      return Promise.resolve();
    }
  }

  onConnectionStateChanged(): Observable<AdapterConnectionStateEnum> {
    return this.onConnectionStateChanged$;
  }

  /*
  private arrayBufferToString(buffer: ArrayBuffer): string {
    return this.decoder.decode(new Uint8Array(buffer));
  }

  private stringToArrayBuffer(str: string): ArrayBuffer {
    const array: Uint8Array = this.encoder.encode(str);
    const buffer: ArrayBuffer = array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset);
    return buffer;
  }
  */
}
